import React from "react";
import {
  Page,
  Text,
  Image,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { fmtEuro, libelleVrFinale } from "./recapData";

Font.register({
  family: "Lato Bold",
  src: `/Lato-Bold.ttf`,
});

const styles = StyleSheet.create({
  textSmall: { fontSize: 7 },
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    borderBottomColor: "#d2d2d2",
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
  },
  rowHead: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    marginTop: 5,
    fontFamily: "Lato Bold",
    borderBottomColor: "black",
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
  },
  column: {
    display: "flex",
    flexDirection: "column",
    fontSize: 8,
    borderColor: "#d2d2d2",
    borderStyle: "solid",
    borderWidth: 1,
    padding: 3,
  },
  columnHead: {
    display: "flex",
    flexDirection: "column",
    fontSize: 8,
    fontFamily: "Lato Bold",
    borderColor: "black",
    borderStyle: "solid",
    borderWidth: 1,
    padding: 1,
    textAlign: "center",
  },
});

// Reçoit des lignes DÉJÀ calculées (cf. buildRecapRows). Aucun hook : peut être
// rendu par `pdf(<PaymentTableDocument rows=… />).toBlob()` hors React.
const PaymentTableDocument = ({ rows, title }) => {
  const totals = {
    priceHT: 0,
    optHT: 0,
    marHT: 0,
    cgCarb: 0,
    bm: 0,
    tva: 0,
    priceTTC: 0,
    investAmtHT: 0,
    p1Label: "",
    p1Value: 0,
    p2Label: "",
    p2Value: 0,
    p3Label: "",
    p3Value: 0,
  };

  // Le nombre de colonnes doit couvrir le devis ayant le plus de périodes :
  // se baser sur rows[0] seul masquait les périodes ajoutées à un autre devis.
  const periodsCount = rows.reduce(
    (max, r) => Math.max(max, r.periods?.length || 0),
    0,
  );
  const colsmw = Math.floor((100 - periodsCount * 10) / 10) - 0.5;
  const collgw =
    periodsCount > 0 ? Math.floor((100 - colsmw * 10) / periodsCount) : 0;

  const getLine = (row, idx) => {
    const content = [];
    for (let i = 0; i < row.quantity; i++) {
      totals.priceHT += row.priceHT;
      totals.optHT += row.optHT;
      totals.marHT += row.marHT;
      totals.cgCarb += row.cgCarb;
      totals.bm += row.bm;
      totals.tva += row.tva;
      totals.priceTTC += row.priceTTC;
      totals.investAmtHT += row.investAmtHT;

      content.push(
        <View key={"r" + idx + i} style={styles.row}>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {row.reference}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {row.typeName}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.priceHT)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.optHT)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.marHT)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.cgCarb)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.bm)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.tva)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.priceTTC)}
          </Text>
          <Text style={{ ...styles.column, width: colsmw + "%" }}>
            {fmtEuro(row.investAmtHT)}
          </Text>

          {row.periods.map((p, k) => {
            const pk = k + 1;
            const isLast = k === row.periods.length - 1;
            const useResidualValue = isLast && !!p.residualValue;
            if (useResidualValue) {
              totals["p" + pk + "Label"] = "v.residuelle";
              totals["p" + pk + "Value"] += parseFloat(p.residualValue);
            } else {
              totals["p" + pk + "Label"] =
                "VR " + (row.typeId == 2 ? "HT" : "TTC") + " : ";
              totals["p" + pk + "Value"] += parseFloat(p.vr);
            }
            return (
              <View
                key={"p" + idx + i + k}
                style={{ ...styles.column, width: collgw + "%" }}
              >
                <Text>
                  Durée : {p.duration != "" ? p.duration + " mois" : ""}
                </Text>
                {useResidualValue ? (
                  <View>
                    {/* LS-682 — VR finale « telle que saisie » : pourcentage
                        (couple historique ou taux VR manuel LS-670) sans
                        contre-valeur en euros, montant en euros sinon. */}
                    <Text>v. residuelle : {libelleVrFinale(p)}</Text>
                  </View>
                ) : (
                  <Text>
                    VR {row.typeId == 2 ? "HT" : "TTC"} :{fmtEuro(p.vr)}
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      );
    }
    return content;
  };

  return (
    <Document title={title}>
      <Page orientation="landscape" size="A4" style={{ padding: 30 }}>
        <View style={{ flexDirection: "row", marginBottom: 15 }}>
          <View style={{ flexDirection: "column", width: "25%" }}>
            <Image src="/logo.png" style={{ width: "70%" }} />
            <Text style={styles.textSmall}>Tel : 0262 21 20 20</Text>
            <Text style={styles.textSmall}>Fax : 0262 46 28 99</Text>
          </View>
          <View
            style={{
              flexDirection: "column",
              width: "50%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text>Demande de financement</Text>
          </View>
        </View>

        <View style={styles.rowHead}>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Véhicule
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>Type</Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Prix net HT
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Options HT
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            MAR HT
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            CG + Carburant
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Bonus/Malus
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>TVA</Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Prix net TTC
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            Montant invest HT
          </Text>
          <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
            Période 1
          </Text>
          {periodsCount > 1 && (
            <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
              Période 2
            </Text>
          )}
          {periodsCount == 3 && (
            <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
              Période 3
            </Text>
          )}
        </View>

        {rows.map((row, idx) => getLine(row, idx))}

        <View style={styles.row}>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            TOTAUX
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>-</Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.priceHT)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.optHT)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.marHT)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.cgCarb)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.bm)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.tva)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.priceTTC)}
          </Text>
          <Text style={{ ...styles.columnHead, width: colsmw + "%" }}>
            {fmtEuro(totals.investAmtHT)}
          </Text>
          <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
            {totals.p1Label + " " + fmtEuro(totals.p1Value)}
          </Text>
          {periodsCount > 1 && (
            <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
              {totals.p2Label +
                " " +
                (totals.p2Value > 0 ? fmtEuro(totals.p2Value) : "")}
            </Text>
          )}
          {periodsCount == 3 && (
            <Text style={{ ...styles.columnHead, width: collgw + "%" }}>
              {totals.p3Label +
                " " +
                (totals.p3Value > 0 ? fmtEuro(totals.p3Value) : "")}
            </Text>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default PaymentTableDocument;
